// API configuration and service functions
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear storage and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      // Could trigger a global auth state update here
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// User API functions
export interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface CreateUser {
  username: string;
  email: string;
  full_name?: string;
  password: string;
  is_admin?: boolean;
}

export const userApi = {
  // Get all users
  getUsers: async (skip: number = 0, limit: number = 100): Promise<User[]> => {
    const response = await api.get(`/api/users/?skip=${skip}&limit=${limit}`);
    return response.data as User[];
  },

  // Get user by ID
  getUser: async (userId: number): Promise<User> => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data as User;
  },

  // Create new user
  createUser: async (user: CreateUser): Promise<User> => {
    const response = await api.post('/api/users/', user);
    return response.data as User;
  },

  // Update user
  updateUser: async (userId: number, user: CreateUser): Promise<User> => {
    const response = await api.put(`/api/users/${userId}`, user);
    return response.data as User;
  },

  // Delete user
  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/api/users/${userId}`);
  },

  // Login user
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post('/api/users/login', credentials);
    return response.data as LoginResponse;
  },
};

// File API functions
export interface FileRecord {
  id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  content_type: string;
  blob_url: string;
  blob_path: string;
  document_type?: string;
  uploaded_by?: number;
  application_id: string;
  created_at: string;
}

export interface FileListResponse {
  files: FileRecord[];
  total: number;
}

export const fileApi = {
  // Upload file (legacy)
  uploadFile: async (file: File, uploadedBy?: number): Promise<FileRecord> => {
    const formData = new FormData();
    formData.append('file', file);
    if (uploadedBy) {
      formData.append('uploaded_by', uploadedBy.toString());
    }

    const response = await api.post('/api/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data as FileRecord;
  },

  // Upload file to specific application
  uploadFileToApplication: async (
    applicationId: string,
    file: File,
    documentType?: string,
    uploadedBy?: number
  ): Promise<FileRecord> => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentType) {
      formData.append('document_type', documentType);
    }
    if (uploadedBy) {
      formData.append('uploaded_by', uploadedBy.toString());
    }

    const response = await api.post(`/api/files/${applicationId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data as FileRecord;
  },

  // Get all files (legacy)
  getFiles: async (skip: number = 0, limit: number = 100): Promise<FileListResponse> => {
    const response = await api.get(`/api/files/?skip=${skip}&limit=${limit}`);
    return response.data as FileListResponse;
  },

  // Get files for specific application
  getApplicationFiles: async (
    applicationId: string,
    skip: number = 0,
    limit: number = 100
  ): Promise<FileListResponse> => {
    const response = await api.get(`/api/files/${applicationId}?skip=${skip}&limit=${limit}`);
    return response.data as FileListResponse;
  },

  // Get file by ID (legacy)
  getFile: async (fileId: number): Promise<FileRecord> => {
    const response = await api.get(`/api/files/${fileId}`);
    return response.data as FileRecord;
  },

  // Download file (legacy)
  downloadFile: async (fileId: number): Promise<Blob> => {
    const response = await api.get(`/api/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // Download file from application
  downloadApplicationFile: async (applicationId: string, fileId: number): Promise<Blob> => {
    const response = await api.get(`/api/files/${applicationId}/download/${fileId}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // Delete file (legacy)
  deleteFile: async (fileId: number): Promise<void> => {
    await api.delete(`/api/files/${fileId}`);
  },

  // Delete file from application
  deleteApplicationFile: async (applicationId: string, fileId: number): Promise<void> => {
    await api.delete(`/api/files/${applicationId}/file/${fileId}`);
  },
};

// Application API functions
export interface LoanApplication {
  id: number;
  application_id: string;
  
  // Legacy field
  applicant_name?: string;
  
  // Personal Details
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  date_of_birth?: string;
  ssn?: string;
  
  // Contact Information
  applicant_email?: string;
  phone_number?: string;
  street_address?: string;
  city?: string;
  state_province?: string;
  zip_code?: string;
  
  // Employment & Income Information
  employment_status?: string;
  primary_income_source?: string;
  annual_income?: number;
  
  // Loan Request Details
  loan_amount?: number;
  loan_term?: string;
  loan_purpose?: string;
  
  // Terms and Conditions
  terms_accepted?: boolean;
  
  // Application Status and Metadata
  status: string;
  notes?: string;
  underwriter_note?: string;
  created_at: string;
  updated_at: string;
  file_count: number;
}

export interface CreateLoanApplication {
  // Legacy field
  applicant_name?: string;
  
  // Personal Details
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  date_of_birth?: string;
  ssn?: string;
  
  // Contact Information
  applicant_email?: string;
  phone_number?: string;
  street_address?: string;
  city?: string;
  state_province?: string;
  zip_code?: string;
  
  // Employment & Income Information
  employment_status?: string;
  primary_income_source?: string;
  annual_income?: number;
  
  // Loan Request Details
  loan_amount?: number;
  loan_term?: string;
  loan_purpose?: string;
  
  // Terms and Conditions
  terms_accepted?: boolean;
  
  // Application Notes
  notes?: string;
  underwriter_note?: string;
}

export interface UpdateLoanApplication {
  // Legacy field
  applicant_name?: string;
  
  // Personal Details
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  date_of_birth?: string;
  ssn?: string;
  
  // Contact Information
  applicant_email?: string;
  phone_number?: string;
  street_address?: string;
  city?: string;
  state_province?: string;
  zip_code?: string;
  
  // Employment & Income Information
  employment_status?: string;
  primary_income_source?: string;
  annual_income?: number;
  
  // Loan Request Details
  loan_amount?: number;
  loan_term?: string;
  loan_purpose?: string;
  
  // Terms and Conditions
  terms_accepted?: boolean;
  
  // Application Status and Notes
  status?: string;
  notes?: string;
  underwriter_note?: string;
}

export interface ApplicationListResponse {
  applications: LoanApplication[];
  total: number;
}

export const applicationApi = {
  // Create new loan application
  createApplication: async (application: CreateLoanApplication): Promise<LoanApplication> => {
    const response = await api.post('/api/applications/', application);
    return response.data as LoanApplication;
  },

  // Get all applications
  getApplications: async (
    skip: number = 0,
    limit: number = 100,
    statusFilter?: string
  ): Promise<ApplicationListResponse> => {
    let url = `/api/applications/?skip=${skip}&limit=${limit}`;
    if (statusFilter) {
      url += `&status_filter=${statusFilter}`;
    }
    const response = await api.get(url);
    return response.data as ApplicationListResponse;
  },

  // Get application by ID
  getApplication: async (applicationId: string): Promise<LoanApplication> => {
    const response = await api.get(`/api/applications/${applicationId}`);
    return response.data as LoanApplication;
  },

  // Update application
  updateApplication: async (
    applicationId: string,
    updates: UpdateLoanApplication
  ): Promise<LoanApplication> => {
    const response = await api.put(`/api/applications/${applicationId}`, updates);
    return response.data as LoanApplication;
  },

  // Delete application
  deleteApplication: async (applicationId: string): Promise<void> => {
    await api.delete(`/api/applications/${applicationId}`);
  },

  // Generate manifest file
  generateManifest: async (applicationId: string): Promise<any> => {
    const response = await api.post(`/api/applications/${applicationId}/generate-manifest`);
    return response.data;
  },

  // Get application summary
  getApplicationSummary: async (applicationId: string) => {
    const response = await api.get(`/api/applications/${applicationId}/summary`);
    return response.data;
  },

  // Get analysis data for application
  getAnalysisData: async (applicationId: string): Promise<any> => {
    const response = await api.get(`/api/applications/${applicationId}/analysis`);
    return response.data;
  },

  // Update underwriter note only (admin only)
  updateUnderwriterNote: async (applicationId: string, underwriterNote: string): Promise<LoanApplication> => {
    const response = await api.put(`/api/applications/${applicationId}/underwriter-note`, {
      underwriter_note: underwriterNote
    });
    return response.data as LoanApplication;
  },
};
